import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationService, MessageService, SortEvent } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { MasterEmpresaService } from '../../../services/master/master-empresa.service';
import { UsuarioService } from '../../../services/usuario/usuario.service';
import { PessoaService } from '../../../services/pessoas/pessoas.service';
import { PlanosService } from '../../../services/planos/planos.service';
import { ContratacaoUsuariosService } from '../../../services/usuario/contratacao-usuarios.service';
import { MasterContextoService } from '../../../services/master/master-contexto.service';
import { IntencaoInscricao, Plano, SimulacaoContratacaoUsuarios } from '../../../models/planos/plano.model';
import { forkJoin, interval, of, Subscription as RxSubscription } from 'rxjs';
import { catchError, switchMap, take } from 'rxjs/operators';
import {
  FormaCobrancaPlanoEspecial,
  MasterCicloHistoricoDTO,
  MasterEmpresaResumoDTO,
  MasterInscricaoVigenteDTO,
  MasterLimiteUsuariosDTO,
  MasterLimiteVendasDTO,
  MasterUsuarioDTO,
  PlanoEspecialMasterDTO,
} from '../../../models/master/master-empresa.model';
import { MasterSessaoDTO } from '../../../models/master/master-sessao.model';
import { TableColumn } from '../../../models/shared/table-column.model';

/**
 * Area Master — ficha da empresa de um cliente.
 *
 * Mostra configuracao e assinatura da empresa alvo, que vem do id da rota e nunca da sessao: o
 * master continua logado na propria empresa o tempo todo. O cabecalho deixa a empresa alvo
 * sempre visivel, para nao restar duvida sobre quem esta sendo consultado.
 */
@Component({
  selector: 'app-master-empresa-detalhe',
  templateUrl: './master-empresa-detalhe.component.html',
  styleUrls: ['./master-empresa-detalhe.component.scss'],
  providers: [MessageService, ConfirmationService],
})
export class MasterEmpresaDetalheComponent implements OnInit, OnDestroy {
  texts: any = {};
  labels: any = {};
  abas: any = {};
  usuariosTexts: any = {};
  concessaoTexts: any = {};
  cobrancaTexts: any = {};

  empresaId!: number;
  resumo: MasterEmpresaResumoDTO | null = null;
  loading = true;

  // --- Historico de ciclos (aba Assinatura) ---

  /**
   * Ciclos de 30 dias da empresa, do mais recente para o mais antigo. Alimentam o seletor de
   * periodo e a tabela do historico; vazio enquanto nao se abre a aba Assinatura.
   */
  ciclos: MasterCicloHistoricoDTO[] = [];
  ciclosLoading = false;
  /** Opcoes do seletor de periodo (mesma ordem de `ciclos`). */
  ciclosOptions: { label: string; value: string }[] = [];
  /**
   * Periodo selecionado, identificado pela data de inicio (ISO). Nulo = ciclo atual, que continua
   * lendo a ficha (`resumo`) — a fonte da verdade do que vale HOJE.
   */
  cicloSelecionadoInicio: string | null = null;

  // --- Aba de usuarios ---
  usuarios: MasterUsuarioDTO[] = [];
  usuariosLoading = false;
  usuariosFirst = 0;
  usuariosRows = 10;
  usuariosTotal = 0;
  usuariosLastfield = 'id';
  usuariosLastSort: 'asc' | 'desc' = 'asc';

  pageSizeOptions = [
    { label: 5, value: 5 },
    { label: 10, value: 10 },
    { label: 20, value: 20 },
    { label: 50, value: 50 },
  ];

  readonly STORAGE_KEY_USUARIOS = 'master-empresa-usuarios-visible-columns';

  colunasUsuarios: TableColumn[] = [
    { key: 'nome', labelKey: 'master.usuarios.labels.usuario', visible: true, fixed: true, defaultVisible: true },
    { key: 'email', labelKey: 'master.usuarios.labels.email', visible: true, fixed: false, defaultVisible: true },
    { key: 'situacao', labelKey: 'master.usuarios.labels.status', visible: true, fixed: false, defaultVisible: true },
    { key: 'criadoEm', labelKey: 'master.usuarios.labels.data_inclusao', visible: false, fixed: false, defaultVisible: false },
    { key: 'acoes', labelKey: 'master.usuarios.labels.acoes', visible: true, fixed: true, defaultVisible: true },
  ];
  /** Ids com ativar/inativar em andamento, para nao piscar a linha inteira. */
  statusEmProgresso = new Set<number>();

  // Dialogo de senha
  senhaDialogVisible = false;
  usuarioDaSenha: MasterUsuarioDTO | null = null;
  senhaNova = '';
  senhaConfirmacao = '';
  senhaSubmetida = false;
  salvandoSenha = false;

  // --- Aba de sessoes ---
  sessoes: MasterSessaoDTO[] = [];
  sessoesLoading = false;
  /** Ids com encerramento em andamento, para nao piscar a linha inteira. */
  sessoesEmProgresso = new Set<number>();
  sessoesTexts: any = {};

  // --- Concessoes (plano e vagas), sem cobranca ---
  planos: Plano[] = [];
  planosCarregando = false;
  planoDialogVisible = false;
  planoSelecionadoId: number | null = null;
  planoUsuariosPermitidos: number | null = null;
  planoManterUsuariosPagos = true;
  planoMotivo = '';
  planoSubmetido = false;
  salvandoPlano = false;

  // --- Plano especial negociado (faixa, valor, prazo e limite proprios) ---
  especialDialogVisible = false;
  especialCategoria: 'BRONZE' | 'PRATA' | 'OURO' = 'OURO';
  especialValor: number | null = null;
  especialPeriodoDias: number | null = 30;
  especialLimiteDeVendas: number | null = null;
  especialUsuariosPermitidos: number | null = null;
  especialManterUsuariosPagos = true;
  especialMotivo = '';
  especialSubmetido = false;
  salvandoEspecial = false;
  especialTexts: any = {};
  categoriasEspeciais: { label: string; value: 'BRONZE' | 'PRATA' | 'OURO' }[] = [];

  vagasDialogVisible = false;
  vagasUsuariosPermitidos: number | null = null;
  vagasMotivo = '';
  salvandoVagas = false;

  // --- Cobranca da empresa alvo (QR / link da Bancard) ---
  cobrancaDialogVisible = false;
  /** 'PLANO' cobra a assinatura; 'VAGAS' cobra usuarios adicionais avulsos. */
  cobrancaTipo: 'PLANO' | 'VAGAS' | 'ESPECIAL' = 'PLANO';
  cobrancaPlanoId: number | null = null;
  cobrancaQuantidade = 1;
  cobrancaSimulacao: SimulacaoContratacaoUsuarios | null = null;
  cobrancaIntencao: IntencaoInscricao | null = null;
  gerandoCobranca = false;
  cobrancaConfirmada = false;
  private pollingCobrancaSub?: RxSubscription;

  // Dialogo de dados
  edicaoDialogVisible = false;
  usuarioEditando: MasterUsuarioDTO | null = null;
  formUsuario = { nome: '', email: '', telefone: '', ddi: '' };
  edicaoSubmetida = false;
  salvandoUsuario = false;

  private langChangeSub!: Subscription;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private masterEmpresaService: MasterEmpresaService,
    private confirmationService: ConfirmationService,
    private usuarioService: UsuarioService,
    private pessoaService: PessoaService,
    private planosService: PlanosService,
    private contratacaoUsuariosService: ContratacaoUsuariosService,
    private masterContexto: MasterContextoService,
    private messageService: MessageService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.loadTranslations();
    this.carregarPreferenciasDeColunas();
    this.langChangeSub = this.translate.onLangChange.subscribe(() => this.loadTranslations());

    const id = Number(this.route.snapshot.paramMap.get('empresaId'));
    if (!id) {
      this.voltar();
      return;
    }
    this.empresaId = id;
    this.origem = this.route.snapshot.queryParamMap.get('origem');
    this.carregarResumo();
  }

  ngOnDestroy(): void {
    this.langChangeSub?.unsubscribe();
    this.pararPollingCobranca();
    // O contexto morre com a tela: fora daqui, nenhuma requisicao pode sair com a empresa alvo.
    this.masterContexto.limpar();
  }

  private loadTranslations(): void {
    this.texts = {
      titulo: this.translate.instant('master.detalhe.texts.titulo'),
      operando_como: this.translate.instant('master.detalhe.texts.operando_como'),
      carregando: this.translate.instant('master.detalhe.texts.carregando'),
      sem_assinatura: this.translate.instant('master.detalhe.texts.sem_assinatura'),
      sim: this.translate.instant('common.sim'),
      nao: this.translate.instant('common.nao'),
      voltar: this.translate.instant('master.detalhe.botoes.voltar'),
      limite_do_plano: this.translate.instant('master.detalhe.texts.limite_do_plano'),
      historico_titulo: this.translate.instant('master.detalhe.texts.historico_titulo'),
      historico_ajuda: this.translate.instant('master.detalhe.texts.historico_ajuda'),
      historico_carregando: this.translate.instant('master.detalhe.texts.historico_carregando'),
      historico_vazio: this.translate.instant('master.detalhe.texts.historico_vazio'),
      historico_erro: this.translate.instant('master.detalhe.texts.historico_erro'),
      visao_historica: this.translate.instant('master.detalhe.texts.visao_historica'),
      voltar_ao_atual: this.translate.instant('master.detalhe.texts.voltar_ao_atual'),
      limite_do_gratuito_no_periodo: this.translate.instant('master.detalhe.texts.limite_do_gratuito_no_periodo'),
      limite_negociado: this.translate.instant('master.detalhe.texts.limite_negociado'),
      limite_do_gratuito: this.translate.instant('master.detalhe.texts.limite_do_gratuito'),
    };

    this.abas = {
      configuracao: this.translate.instant('master.detalhe.abas.configuracao'),
      assinatura: this.translate.instant('master.detalhe.abas.assinatura'),
      usuarios: this.translate.instant('master.detalhe.abas.usuarios'),
      sessoes: this.translate.instant('master.detalhe.abas.sessoes'),
    };

    this.especialTexts = {
      titulo: this.translate.instant('master.especial.texts.titulo'),
      explicacao: this.translate.instant('master.especial.texts.explicacao'),
      abrir: this.translate.instant('master.especial.botoes.abrir'),
      conceder: this.translate.instant('master.especial.botoes.conceder'),
      cobrar_qr: this.translate.instant('master.especial.botoes.cobrar_qr'),
      cobrar_cartao: this.translate.instant('master.especial.botoes.cobrar_cartao'),
      categoria: this.translate.instant('master.especial.labels.categoria'),
      valor: this.translate.instant('master.especial.labels.valor'),
      valor_ajuda: this.translate.instant('master.especial.labels.valor_ajuda'),
      periodo: this.translate.instant('master.especial.labels.periodo'),
      periodo_ajuda: this.translate.instant('master.especial.labels.periodo_ajuda'),
      limite: this.translate.instant('master.especial.labels.limite'),
      limite_ajuda: this.translate.instant('master.especial.labels.limite_ajuda'),
    };

    this.categoriasEspeciais = [
      { label: this.translate.instant('master.especial.categorias.bronze'), value: 'BRONZE' },
      { label: this.translate.instant('master.especial.categorias.prata'), value: 'PRATA' },
      { label: this.translate.instant('master.especial.categorias.ouro'), value: 'OURO' },
    ];

    this.sessoesTexts = {
      titulo: this.translate.instant('master.sessoes.texts.titulo'),
      explicacao: this.translate.instant('master.sessoes.texts.explicacao'),
      nenhuma: this.translate.instant('master.sessoes.texts.nenhuma'),
      carregando: this.translate.instant('master.sessoes.texts.carregando'),
      atualizar: this.translate.instant('master.sessoes.botoes.atualizar'),
      encerrar: this.translate.instant('master.sessoes.botoes.encerrar'),
      usuario: this.translate.instant('master.sessoes.labels.usuario'),
      plataforma: this.translate.instant('master.sessoes.labels.plataforma'),
      aparelho: this.translate.instant('master.sessoes.labels.aparelho'),
      situacao: this.translate.instant('master.sessoes.labels.situacao'),
      inicio: this.translate.instant('master.sessoes.labels.inicio'),
      ultimo_uso: this.translate.instant('master.sessoes.labels.ultimo_uso'),
      expira_em: this.translate.instant('master.sessoes.labels.expira_em'),
      acoes: this.translate.instant('master.sessoes.labels.acoes'),
      ocupando: this.translate.instant('master.sessoes.labels.ocupando'),
      liberada: this.translate.instant('master.sessoes.labels.liberada'),
      sem_aparelho: this.translate.instant('master.sessoes.labels.sem_aparelho'),
    };

    this.labels = {
      // Empresa
      nome: this.translate.instant('master.detalhe.labels.nome'),
      fantasia: this.translate.instant('master.detalhe.labels.fantasia'),
      ruc: this.translate.instant('master.detalhe.labels.ruc'),
      email: this.translate.instant('master.detalhe.labels.email'),
      telefone: this.translate.instant('master.detalhe.labels.telefone'),
      cidade: this.translate.instant('master.detalhe.labels.cidade'),
      moeda: this.translate.instant('master.detalhe.labels.moeda'),
      cliente_desde: this.translate.instant('master.detalhe.labels.cliente_desde'),
      contador: this.translate.instant('master.detalhe.labels.contador'),
      primeiros_passos: this.translate.instant('master.detalhe.labels.primeiros_passos'),
      minishop: this.translate.instant('master.detalhe.labels.minishop'),
      cobranca_qrcode: this.translate.instant('master.detalhe.labels.cobranca_qrcode'),
      cobranca_cartao: this.translate.instant('master.detalhe.labels.cobranca_cartao'),
      nota_eletronica: this.translate.instant('master.detalhe.labels.nota_eletronica'),
      certificado: this.translate.instant('master.detalhe.labels.certificado'),
      certificado_vence: this.translate.instant('master.detalhe.labels.certificado_vence'),
      // Assinatura
      plano: this.translate.instant('master.detalhe.labels.plano'),
      situacao: this.translate.instant('master.detalhe.labels.situacao'),
      contratado_em: this.translate.instant('master.detalhe.labels.contratado_em'),
      vence_em: this.translate.instant('master.detalhe.labels.vence_em'),
      meses_restantes: this.translate.instant('master.detalhe.labels.meses_restantes'),
      valor: this.translate.instant('master.detalhe.labels.valor'),
      forma_pagamento: this.translate.instant('master.detalhe.labels.forma_pagamento'),
      periodicidade: this.translate.instant('master.detalhe.labels.periodicidade'),
      valor_do_plano: this.translate.instant('master.detalhe.labels.valor_do_plano'),
      desconto: this.translate.instant('master.detalhe.labels.desconto'),
      // Limite de vendas
      limite_de_vendas: this.translate.instant('master.detalhe.labels.limite_de_vendas'),
      limite_mensal: this.translate.instant('master.detalhe.labels.limite_mensal'),
      vendido_no_ciclo: this.translate.instant('master.detalhe.labels.vendido_no_ciclo'),
      disponivel_para_vender: this.translate.instant('master.detalhe.labels.disponivel_para_vender'),
      uso_do_limite: this.translate.instant('master.detalhe.labels.uso_do_limite'),
      ciclo_atual: this.translate.instant('master.detalhe.labels.ciclo_atual'),
      periodo: this.translate.instant('master.detalhe.labels.periodo'),
      ciclo_em_curso: this.translate.instant('master.detalhe.labels.ciclo_em_curso'),
      periodo_do_ciclo: this.translate.instant('master.detalhe.labels.periodo_do_ciclo'),
      acessos_no_periodo: this.translate.instant('master.detalhe.labels.acessos_no_periodo'),
      situacao_no_periodo: this.translate.instant('master.detalhe.labels.situacao_no_periodo'),
      sem_vigencia: this.translate.instant('master.detalhe.labels.sem_vigencia'),
      col_cobranca: this.translate.instant('master.detalhe.labels.col_cobranca'),
      col_vendido: this.translate.instant('master.detalhe.labels.col_vendido'),
      col_uso: this.translate.instant('master.detalhe.labels.col_uso'),
      sem_limite: this.translate.instant('master.detalhe.labels.sem_limite'),
      negociado: this.translate.instant('master.detalhe.labels.negociado'),
      // Acessos
      acessos: this.translate.instant('master.detalhe.labels.acessos'),
      inclusos: this.translate.instant('master.detalhe.labels.inclusos'),
      comprados: this.translate.instant('master.detalhe.labels.comprados'),
      concedidos: this.translate.instant('master.detalhe.labels.concedidos'),
      limite: this.translate.instant('master.detalhe.labels.limite'),
      em_uso: this.translate.instant('master.detalhe.labels.em_uso'),
      disponiveis: this.translate.instant('master.detalhe.labels.disponiveis'),
      vigente: this.translate.instant('master.detalhe.labels.vigente'),
      nunca_vence: this.translate.instant('master.detalhe.labels.nunca_vence'),
      vencida: this.translate.instant('master.detalhe.labels.vencida'),
      // Usuarios
      usuario: this.translate.instant('master.usuarios.labels.usuario'),
      status: this.translate.instant('master.usuarios.labels.status'),
      ativo: this.translate.instant('master.usuarios.labels.ativo'),
      inativo: this.translate.instant('master.usuarios.labels.inativo'),
      acoes: this.translate.instant('master.usuarios.labels.acoes'),
      nova_senha: this.translate.instant('master.usuarios.labels.nova_senha'),
      confirmar_senha: this.translate.instant('master.usuarios.labels.confirmar_senha'),
      nome_usuario: this.translate.instant('master.usuarios.labels.nome'),
      email_usuario: this.translate.instant('master.usuarios.labels.email'),
      telefone_usuario: this.translate.instant('master.usuarios.labels.telefone'),
      data_inclusao: this.translate.instant('master.usuarios.labels.data_inclusao'),
      itens_por_pagina: this.translate.instant('master.empresas.labels.itens_por_pagina'),
    };

    this.concessaoTexts = {
      conceder_plano: this.translate.instant('master.assinatura.texts.conceder_plano'),
      conceder_vagas: this.translate.instant('master.assinatura.texts.conceder_vagas'),
      sem_cobranca: this.translate.instant('master.assinatura.texts.sem_cobranca'),
      plano: this.translate.instant('master.assinatura.labels.plano'),
      total_acessos: this.translate.instant('master.assinatura.labels.total_acessos'),
      total_acessos_ajuda: this.translate.instant('master.assinatura.texts.total_acessos_ajuda'),
      manter_pagos: this.translate.instant('master.assinatura.labels.manter_pagos'),
      manter_pagos_ajuda: this.translate.instant('master.assinatura.texts.manter_pagos_ajuda'),
      motivo: this.translate.instant('master.assinatura.labels.motivo'),
      alvo: this.translate.instant('master.assinatura.texts.alvo'),
      salvar: this.translate.instant('common.salvar'),
      cancelar: this.translate.instant('common.cancelar'),
    };

    this.cobrancaTexts = {
      titulo_plano: this.translate.instant('master.cobranca.texts.titulo_plano'),
      titulo_vagas: this.translate.instant('master.cobranca.texts.titulo_vagas'),
      explicacao: this.translate.instant('master.cobranca.texts.explicacao'),
      aguardando: this.translate.instant('master.cobranca.texts.aguardando'),
      confirmado: this.translate.instant('master.cobranca.texts.confirmado'),
      quantidade: this.translate.instant('master.cobranca.labels.quantidade'),
      valor: this.translate.instant('master.cobranca.labels.valor'),
      gerar_qr: this.translate.instant('master.cobranca.botoes.gerar_qr'),
      gerar_link: this.translate.instant('master.cobranca.botoes.gerar_link'),
      copiar: this.translate.instant('master.cobranca.botoes.copiar'),
      abrir_link: this.translate.instant('master.cobranca.botoes.abrir_link'),
      fechar: this.translate.instant('master.cobranca.botoes.fechar'),
      cobrar_plano: this.translate.instant('master.cobranca.botoes.cobrar_plano'),
      cobrar_vagas: this.translate.instant('master.cobranca.botoes.cobrar_vagas'),
    };

    this.usuariosTexts = {
      titulo: this.translate.instant('master.usuarios.texts.titulo'),
      nenhum: this.translate.instant('master.usuarios.texts.nenhum'),
      carregando: this.translate.instant('master.usuarios.texts.carregando'),
      alterar_senha: this.translate.instant('master.usuarios.texts.alterar_senha'),
      editar_dados: this.translate.instant('master.usuarios.texts.editar_dados'),
      senha_aviso: this.translate.instant('master.usuarios.texts.senha_aviso'),
      salvar: this.translate.instant('common.salvar'),
      cancelar: this.translate.instant('common.cancelar'),
    };

    // Os rótulos dos ciclos carregam o "(em curso)" traduzido: refaz na troca de idioma.
    this.montarOpcoesDeCiclo();
  }

  carregarResumo(): void {
    this.loading = true;
    this.masterEmpresaService.consultarResumo(this.empresaId).subscribe({
      next: (resumo) => {
        this.resumo = resumo;
        this.loading = false;
        // So a partir daqui as rotas de cobranca podem sair com o header da empresa alvo.
        this.masterContexto.selecionar({
          empresaId: resumo.empresa.id,
          nome: resumo.empresa.nome,
        });
      },
      error: (err) => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail: err?.error?.message || this.translate.instant('master.detalhe.mensagens.erro_carregar'),
        });
      },
    });
  }

  /**
   * Tela de origem, lida do queryParam `origem`. A ficha e alcancada de tres
   * listagens diferentes; voltar sempre para empresas jogava quem veio de
   * assinaturas ou de usuarios em uma lista que ele nunca abriu.
   *
   * E queryParam, e nao `Location.back()`, porque a volta precisa funcionar
   * igual depois de um F5 ou de um link colado.
   */
  private origem: string | null = null;

  private static readonly ROTA_POR_ORIGEM: Record<string, string> = {
    planos: '/master/planos',
    usuarios: '/master/usuarios',
  };

  voltar(): void {
    const rota = (this.origem && MasterEmpresaDetalheComponent.ROTA_POR_ORIGEM[this.origem])
      || '/master/empresas';
    this.router.navigate([rota]);
  }

  /** Rótulo da situação da assinatura: plano perpétuo não vence, o resto depende da vigência. */
  get situacaoDaAssinatura(): string {
    const ciclo = this.cicloExibido;
    if (ciclo) {
      if (ciclo.especial) return this.labels.nunca_vence;
      return ciclo.assinaturaVigente ? this.labels.vigente : this.labels.sem_vigencia;
    }
    const inscricao = this.resumo?.inscricao;
    if (!inscricao) return this.texts.sem_assinatura;
    if (inscricao.especial) return this.labels.nunca_vence;
    return inscricao.vigente ? this.labels.vigente : this.labels.vencida;
  }

  // =========================================================================
  // Histórico de ciclos
  // =========================================================================

  /**
   * O ciclo passado que está selecionado, ou null quando a tela mostra o ciclo atual.
   *
   * O atual continua vindo da ficha (`resumo`) de propósito: é ele que o sistema aplica agora, e
   * a ficha é a fonte da verdade do presente. O histórico responde só pelo que já fechou.
   */
  get cicloExibido(): MasterCicloHistoricoDTO | null {
    if (!this.cicloSelecionadoInicio) return null;
    return this.ciclos.find((c) => c.inicio === this.cicloSelecionadoInicio && !c.atual) ?? null;
  }

  /** A tela está mostrando um período já encerrado. */
  get visaoHistorica(): boolean {
    return this.cicloExibido != null;
  }

  /**
   * Assinatura a exibir nos cards: a vigente, ou a que valia no ciclo escolhido.
   *
   * Reconstrói o mesmo formato do resumo para o template continuar um só — duplicar os cards
   * numa versão "histórica" criaria duas telas para manter em sincronia.
   */
  get inscricaoExibida(): MasterInscricaoVigenteDTO | null {
    const ciclo = this.cicloExibido;
    if (!ciclo) return this.resumo?.inscricao ?? null;
    return {
      inscricaoId: ciclo.inscricaoId,
      planoId: ciclo.planoId,
      planoNome: ciclo.planoNome,
      tipoPlano: ciclo.tipoPlano,
      especial: ciclo.especial,
      planoGratuito: ciclo.planoGratuito,
      dataDeInscricao: ciclo.dataDeInscricao,
      dataDeVencimento: ciclo.dataDeVencimento,
      vigente: ciclo.assinaturaVigente,
      status: null,
      tipoContratacao: ciclo.tipoContratacao,
      tipoPagamento: ciclo.tipoPagamento,
      valorPlano: ciclo.valorPlano,
      valorDeCobranca: ciclo.valorDeCobranca,
      porcentagemDesconto: ciclo.porcentagemDesconto,
      usuariosInclusos: ciclo.usuariosInclusos,
      usuariosExtras: ciclo.usuariosExtras,
      usuariosCortesia: ciclo.usuariosCortesia,
      limiteDeUsuarios: ciclo.limiteDeUsuarios,
      // Conceito do presente: quantos meses ainda restam. Num ciclo fechado não significa nada,
      // e o card é escondido na visão histórica.
      mesesRestantes: 0,
    } as MasterInscricaoVigenteDTO;
  }

  /** Limite de vendas a exibir: o de hoje, ou o que valia no ciclo escolhido. */
  get limiteDeVendasExibido(): MasterLimiteVendasDTO | null {
    const ciclo = this.cicloExibido;
    if (!ciclo) return this.resumo?.limiteDeVendas ?? null;
    return {
      limiteAplicado: ciclo.limiteAplicado,
      limiteContratado: null,
      semLimite: ciclo.semLimite,
      negociado: ciclo.negociado,
      vendasNoCiclo: ciclo.vendasNoPeriodo,
      disponivel: ciclo.disponivel,
      percentualUsado: ciclo.percentualUsado,
      inicioDoCiclo: ciclo.inicio,
      reiniciaEm: ciclo.fim,
      moedaDaEmpresa: ciclo.moedaDaEmpresa,
    } as MasterLimiteVendasDTO;
  }

  /**
   * Acessos a exibir: os de hoje, ou os que a contratação dava no ciclo escolhido.
   *
   * Quantos usuários estavam ATIVOS naquele mês não existe no banco — só o estado atual —, então
   * na visão histórica a tela mostra apenas a composição do limite e esconde uso e vagas livres.
   */
  get acessosExibidos(): MasterLimiteUsuariosDTO | null {
    const ciclo = this.cicloExibido;
    if (!ciclo) return this.resumo?.limiteDeUsuarios ?? null;
    return {
      usuariosInclusos: ciclo.usuariosInclusos,
      usuariosExtras: ciclo.usuariosExtras,
      usuariosCortesia: ciclo.usuariosCortesia,
      limite: ciclo.limiteDeUsuarios,
      usuariosAtivos: 0,
      vagasDisponiveis: 0,
      podeCriarUsuario: false,
      valorPorUsuarioMensal: 0,
      mesesRestantes: 0,
      valorPorUsuarioAteFimDoPlano: 0,
      podeContratarUsuarios: false,
    } as MasterLimiteUsuariosDTO;
  }

  /** "Ciclo atual" no presente; "Ciclo" quando se olha um período fechado. */
  get rotuloDoPeriodoExibido(): string {
    return this.visaoHistorica ? this.labels.periodo_do_ciclo : this.labels.ciclo_atual;
  }

  /** Datas do período exibido, no formato que o rodapé do card mostra. */
  get periodoExibido(): string {
    const ciclo = this.cicloExibido;
    if (ciclo) return `${this.formatarData(ciclo.inicio)} — ${this.formatarData(ciclo.fim)}`;
    const vendas = this.resumo?.limiteDeVendas;
    if (!vendas) return '';
    return `${this.formatarData(vendas.inicioDoCiclo)} — ${this.formatarData(vendas.reiniciaEm)}`;
  }

  /** Rótulo de um ciclo no seletor e na tabela: "dd/MM/yyyy — dd/MM/yyyy". */
  rotuloDoCiclo(ciclo: MasterCicloHistoricoDTO): string {
    const base = `${this.formatarData(ciclo.inicio)} — ${this.formatarData(ciclo.fim)}`;
    return ciclo.atual ? `${base} (${this.labels.ciclo_em_curso})` : base;
  }

  /** dd/MM/yyyy a partir do ISO do backend, sem depender do pipe no TS. */
  private formatarData(iso: string | null | undefined): string {
    if (!iso) return '-';
    const data = new Date(iso);
    if (isNaN(data.getTime())) return '-';
    const dia = String(data.getDate()).padStart(2, '0');
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    return `${dia}/${mes}/${data.getFullYear()}`;
  }

  /**
   * Carrega os ciclos ao abrir a aba Assinatura. Sob demanda (e não junto da ficha) porque cada
   * ciclo custa uma soma de vendas no banco, e quem abre a empresa nem sempre vai ao histórico.
   */
  carregarCiclos(): void {
    this.ciclosLoading = true;
    this.masterEmpresaService.listarHistoricoDeCiclos(this.empresaId).subscribe({
      next: (ciclos) => {
        this.ciclos = ciclos || [];
        this.ciclosLoading = false;
        this.montarOpcoesDeCiclo();
      },
      error: (err) => {
        this.ciclos = [];
        this.ciclosOptions = [];
        this.ciclosLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail: err?.error?.message || this.texts.historico_erro,
        });
      },
    });
  }

  /** Opções do seletor, na mesma ordem dos ciclos (mais recente primeiro). */
  private montarOpcoesDeCiclo(): void {
    this.ciclosOptions = this.ciclos.map((ciclo) => ({
      label: this.rotuloDoCiclo(ciclo),
      value: ciclo.inicio,
    }));
  }

  /** Volta a ficha para o presente. */
  voltarAoCicloAtual(): void {
    this.cicloSelecionadoInicio = null;
  }

  simNao(valor: boolean | null | undefined): string {
    return valor ? this.texts.sim : this.texts.nao;
  }

  /** Periodicidade do plano contratado (Mensal, Anual...). Sem tradução, mostra o próprio código. */
  get periodicidadeDoPlano(): string {
    const tipoPlano = this.inscricaoExibida?.tipoPlano;
    if (!tipoPlano) return '-';
    const chave = `master.detalhe.periodicidade.${tipoPlano.toLowerCase()}`;
    const traduzido = this.translate.instant(chave);
    return traduzido === chave ? tipoPlano : traduzido;
  }

  get temDesconto(): boolean {
    const desconto = this.inscricaoExibida?.porcentagemDesconto;
    return desconto != null && Number(desconto) > 0;
  }

  /**
   * Explica DE ONDE vem o teto que está valendo — sem isso o suporte olha um limite baixo e não
   * entende por que ele não bate com o plano que o cliente contratou.
   */
  get textoDoLimiteAplicado(): string {
    const vendas = this.limiteDeVendasExibido;
    if (!vendas || vendas.semLimite) return '';
    // Num ciclo passado, quem responde pela vigência é o próprio ciclo — a assinatura pode ter
    // vencido depois dele, e dizer "sem vigência" ali seria mentira sobre aquele período.
    const ciclo = this.cicloExibido;
    if (ciclo) {
      if (!ciclo.assinaturaVigente) return this.texts.limite_do_gratuito_no_periodo;
      return vendas.negociado ? this.texts.limite_negociado : this.texts.limite_do_plano;
    }
    // Assinatura vencida: quem manda é o limite do plano gratuito, não o contratado.
    const vencida = !!this.resumo?.inscricao && !this.resumo.inscricao.vigente;
    if (vencida) return this.texts.limite_do_gratuito;
    if (!this.resumo?.inscricao) return this.texts.limite_do_gratuito;
    return vendas.negociado ? this.texts.limite_negociado : this.texts.limite_do_plano;
  }

  // =========================================================================
  // Concessoes sem cobranca (plano e vagas)
  // =========================================================================

  /**
   * Abre a concessao de plano.
   *
   * Os planos ESPECIAIS vem primeiro porque sao os negociados — e o caso mais comum aqui. Falha
   * ao listar os comuns nao impede a concessao de um especial, entao cada lista tem o proprio
   * `catchError` (mesmo desenho da aprovacao de contador).
   */
  abrirDialogPlano(): void {
    this.planoSelecionadoId = this.resumo?.inscricao?.planoId ?? null;
    this.planoUsuariosPermitidos = this.resumo?.limiteDeUsuarios?.limite ?? null;
    this.planoManterUsuariosPagos = (this.resumo?.limiteDeUsuarios?.usuariosExtras ?? 0) > 0;
    this.planoMotivo = '';
    this.planoSubmetido = false;
    this.planoDialogVisible = true;

    if (this.planos.length === 0) this.carregarPlanos();
  }

  /** Vagas pagas que a empresa perderia na troca de plano — a tela avisa antes de trocar. */
  get vagasPagas(): number {
    return this.resumo?.limiteDeUsuarios?.usuariosExtras ?? 0;
  }

  /** Conceder nunca revoga vaga paga: o minimo do campo e o que a empresa ja tem garantido. */
  get minimoDeAcessos(): number {
    const limite = this.resumo?.limiteDeUsuarios;
    if (!limite) return 1;
    return Math.max(1, limite.usuariosInclusos + limite.usuariosExtras);
  }

  concederPlano(): void {
    this.planoSubmetido = true;
    if (!this.planoSelecionadoId) return;

    this.salvandoPlano = true;
    this.masterEmpresaService.atribuirPlano(this.empresaId, {
      planoId: this.planoSelecionadoId,
      usuariosPermitidos: this.planoUsuariosPermitidos ?? null,
      manterUsuariosPagos: this.planoManterUsuariosPagos,
      motivo: this.planoMotivo?.trim() || null,
    }).subscribe({
      next: (resumo) => {
        this.resumo = resumo;
        this.salvandoPlano = false;
        this.planoDialogVisible = false;
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.sucesso'),
          detail: this.translate.instant('master.assinatura.mensagens.plano_concedido'),
        });
      },
      error: (err) => {
        this.salvandoPlano = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail: err?.error?.message || this.translate.instant('master.assinatura.mensagens.erro_plano'),
        });
      },
    });
  }

  // =========================================================================
  // Plano especial negociado
  // =========================================================================

  /**
   * Abre o pacote negociado ja preenchido com o que a empresa tem hoje: o total de acessos e o
   * limite de vendas atuais servem de ponto de partida da negociacao.
   */
  abrirDialogPlanoEspecial(): void {
    this.especialCategoria = 'OURO';
    this.especialValor = null;
    this.especialPeriodoDias = 30;
    this.especialLimiteDeVendas = null;
    this.especialUsuariosPermitidos = this.resumo?.limiteDeUsuarios?.limite ?? null;
    this.especialManterUsuariosPagos = true;
    this.especialMotivo = '';
    this.especialSubmetido = false;
    this.especialDialogVisible = true;
  }

  /** Campos obrigatorios do pacote: sem valor, prazo e limite nao ha condicao a conceder. */
  private planoEspecialValido(): boolean {
    this.especialSubmetido = true;
    return (
      this.especialValor !== null && this.especialValor >= 0 &&
      !!this.especialPeriodoDias && this.especialPeriodoDias > 0 &&
      this.especialLimiteDeVendas !== null && this.especialLimiteDeVendas >= 0
    );
  }

  private montarPlanoEspecial(): PlanoEspecialMasterDTO {
    return {
      categoria: this.especialCategoria,
      valor: this.especialValor ?? 0,
      periodoDias: this.especialPeriodoDias ?? 0,
      limiteDeVendas: this.especialLimiteDeVendas ?? 0,
      usuariosPermitidos: this.especialUsuariosPermitidos ?? null,
      manterUsuariosPagos: this.especialManterUsuariosPagos,
      motivo: this.especialMotivo?.trim() || null,
    };
  }

  /** Concede o pacote: vale na hora, sem cobranca. */
  concederPlanoEspecial(): void {
    if (!this.planoEspecialValido()) return;

    this.salvandoEspecial = true;
    this.masterEmpresaService.concederPlanoEspecial(this.empresaId, this.montarPlanoEspecial()).subscribe({
      next: (resumo) => {
        this.resumo = resumo;
        this.salvandoEspecial = false;
        this.especialDialogVisible = false;
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.sucesso'),
          detail: this.translate.instant('master.especial.mensagens.concedido'),
        });
      },
      error: (err) => {
        this.salvandoEspecial = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail: err?.error?.message || this.translate.instant('master.especial.mensagens.erro_conceder'),
        });
      },
    });
  }

  /**
   * Cobra o mesmo pacote. O plano so passa a valer quando a Bancard confirmar, entao aqui a
   * tela apenas troca para o dialogo de cobranca, que ja sabe mostrar QR/link e acompanhar o
   * pagamento.
   */
  cobrarPlanoEspecial(forma: FormaCobrancaPlanoEspecial): void {
    if (!this.planoEspecialValido()) return;

    this.salvandoEspecial = true;
    this.masterEmpresaService
      .cobrarPlanoEspecial(this.empresaId, this.montarPlanoEspecial(), forma)
      .subscribe({
        next: (intencao) => {
          this.salvandoEspecial = false;
          this.especialDialogVisible = false;
          // A intencao ja nasce aqui: o dialogo de cobranca abre direto no resultado, sem a
          // etapa de escolher o que cobrar.
          this.cobrancaTipo = 'ESPECIAL';
          this.cobrancaConfirmada = false;
          this.cobrancaIntencao = intencao;
          this.cobrancaDialogVisible = true;
          this.iniciarPollingCobranca(intencao);
        },
        error: (err) => {
          this.salvandoEspecial = false;
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('common.erro'),
            detail: err?.error?.message || this.translate.instant('master.especial.mensagens.erro_cobrar'),
          });
        },
      });
  }

  abrirDialogVagas(): void {
    this.vagasUsuariosPermitidos = this.resumo?.limiteDeUsuarios?.limite ?? null;
    this.vagasMotivo = '';
    this.vagasDialogVisible = true;
  }

  concederVagas(): void {
    if (!this.vagasUsuariosPermitidos) return;

    this.salvandoVagas = true;
    this.masterEmpresaService.concederUsuarios(this.empresaId, {
      usuariosPermitidos: this.vagasUsuariosPermitidos,
      motivo: this.vagasMotivo?.trim() || null,
    }).subscribe({
      next: (resumo) => {
        this.resumo = resumo;
        this.salvandoVagas = false;
        this.vagasDialogVisible = false;
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.sucesso'),
          detail: this.translate.instant('master.assinatura.mensagens.vagas_concedidas'),
        });
      },
      error: (err) => {
        this.salvandoVagas = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail: err?.error?.message || this.translate.instant('master.assinatura.mensagens.erro_vagas'),
        });
      },
    });
  }

  // =========================================================================
  // Cobranca da empresa alvo (o cliente paga)
  // =========================================================================

  /**
   * Abre a cobranca. O QR e o link nascem NA EMPRESA ALVO, pelo contexto ativo — o master ve o
   * codigo e repassa ao cliente. Nada muda no plano ate a Bancard confirmar.
   */
  abrirDialogCobranca(tipo: 'PLANO' | 'VAGAS'): void {
    this.cobrancaTipo = tipo;
    this.cobrancaIntencao = null;
    this.cobrancaSimulacao = null;
    this.cobrancaConfirmada = false;
    this.cobrancaQuantidade = 1;
    this.cobrancaPlanoId = this.resumo?.inscricao?.planoId ?? null;
    this.pararPollingCobranca();
    this.cobrancaDialogVisible = true;

    if (tipo === 'PLANO' && this.planos.length === 0) {
      this.carregarPlanos();
    }
    if (tipo === 'VAGAS') {
      this.simularVagas();
    }
  }

  private carregarPlanos(): void {
    this.planosCarregando = true;
    forkJoin({
      comuns: this.planosService.listarPlanos().pipe(catchError(() => of([] as Plano[]))),
      especiais: this.planosService.listarEspeciais().pipe(catchError(() => of([] as Plano[]))),
    }).subscribe(({ comuns, especiais }) => {
      this.planos = [...especiais, ...comuns.filter((p) => p.ativo !== false)];
      this.planosCarregando = false;
    });
  }

  /** Previa do valor dos acessos avulsos, ja na empresa alvo (o simular respeita o contexto). */
  simularVagas(): void {
    if (this.cobrancaQuantidade < 1) return;
    this.contratacaoUsuariosService.simular(this.cobrancaQuantidade).subscribe({
      next: (simulacao) => (this.cobrancaSimulacao = simulacao),
      error: () => (this.cobrancaSimulacao = null),
    });
  }

  gerarCobranca(porCartao: boolean): void {
    if (this.cobrancaTipo === 'PLANO' && !this.cobrancaPlanoId) return;

    this.gerandoCobranca = true;
    const requisicao = this.cobrancaTipo === 'PLANO'
      ? (porCartao
          ? this.planosService.assinarPlano(this.cobrancaPlanoId!)
          : this.planosService.gerarQrCode(this.cobrancaPlanoId!))
      : (porCartao
          ? this.contratacaoUsuariosService.gerarLinkTpago(this.cobrancaQuantidade)
          : this.contratacaoUsuariosService.gerarQr(this.cobrancaQuantidade));

    requisicao.subscribe({
      next: (intencao) => {
        this.gerandoCobranca = false;
        this.cobrancaIntencao = intencao;
        this.iniciarPollingCobranca(intencao);
      },
      error: (err) => {
        this.gerandoCobranca = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail: err?.error?.message || this.translate.instant('master.cobranca.mensagens.erro_gerar'),
        });
      },
    });
  }

  /**
   * Acompanha o pagamento por 5 minutos (5s x 60), como as telas do cliente.
   *
   * O polling NAO usa contexto: os endpoints de verificacao ja liberam o master por
   * `checarAcessoDoUsuario`.
   */
  private iniciarPollingCobranca(intencao: IntencaoInscricao): void {
    const alias = intencao?.hookAlias || intencao?.linkAlias;
    if (!alias) return;

    this.pararPollingCobranca();
    // ESPECIAL e uma intencao de PLANO como outra qualquer: quem confirma o pagamento e o
    // mesmo endpoint. Só a contratacao avulsa de vagas tem polling proprio.
    const consulta = this.cobrancaTipo === 'VAGAS'
      ? () => this.contratacaoUsuariosService.verificarPagamento(alias).pipe(catchError(() => of(null)))
      : () => this.planosService.verificarPagamento(alias).pipe(catchError(() => of(null)));

    this.pollingCobrancaSub = interval(5000)
      .pipe(take(60), switchMap(() => consulta()))
      .subscribe((resposta) => {
        if (!resposta) return;
        this.pararPollingCobranca();
        this.cobrancaConfirmada = true;
        this.carregarResumo();
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.sucesso'),
          detail: this.translate.instant('master.cobranca.mensagens.pagamento_confirmado'),
        });
      });
  }

  private pararPollingCobranca(): void {
    this.pollingCobrancaSub?.unsubscribe();
    this.pollingCobrancaSub = undefined;
  }

  fecharCobranca(): void {
    this.pararPollingCobranca();
    this.cobrancaDialogVisible = false;
  }

  /** Copia o payload do QR, para o suporte colar no WhatsApp do cliente. */
  copiarTexto(texto?: string | null): void {
    if (!texto) return;
    navigator.clipboard?.writeText(texto);
    this.messageService.add({
      severity: 'success',
      summary: this.translate.instant('common.sucesso'),
      detail: this.translate.instant('master.cobranca.mensagens.copiado'),
    });
  }

  // =========================================================================
  // Usuarios da empresa
  // =========================================================================

  /** Carrega sob demanda: so quando o suporte abre a aba de usuarios. */
  onTabChange(evento: any): void {
    if (evento?.index === 1 && this.ciclos.length === 0 && !this.ciclosLoading) {
      this.carregarCiclos();
    }
    if (evento?.index === 2 && this.usuarios.length === 0) {
      this.carregarUsuarios();
    }
    if (evento?.index === 3 && this.sessoes.length === 0) {
      this.carregarSessoes();
    }
  }

  isColunaVisivel(key: string): boolean {
    return this.colunasUsuarios.find((c) => c.key === key)?.visible ?? true;
  }

  get colunasVisiveis(): number {
    return this.colunasUsuarios.filter((c) => c.visible).length;
  }

  onColunasChange(colunas: TableColumn[]): void {
    this.colunasUsuarios = [...colunas];
  }

  /** Restaura a escolha de colunas do suporte, como nas demais listagens. */
  private carregarPreferenciasDeColunas(): void {
    try {
      const salvo = localStorage.getItem(this.STORAGE_KEY_USUARIOS);
      if (!salvo) return;
      const visiveis = JSON.parse(salvo) as string[];
      this.colunasUsuarios.forEach((col) => {
        if (!col.fixed) {
          col.visible = visiveis.includes(col.key);
        }
      });
    } catch (e) {
      console.warn('Erro ao carregar preferências de colunas:', e);
    }
  }

  onSortUsuarios(evento: SortEvent): void {
    const campo = evento.field || this.usuariosLastfield;
    const ordem: 'asc' | 'desc' = evento.order === -1 ? 'desc' : 'asc';
    if (campo === this.usuariosLastfield && ordem === this.usuariosLastSort) return;
    this.usuariosLastfield = campo;
    this.usuariosLastSort = ordem;
    this.usuariosFirst = 0;
    this.carregarUsuarios(0);
  }

  onUsuariosRowsPerPageChange(): void {
    this.usuariosFirst = 0;
    this.carregarUsuarios(0, this.usuariosRows);
  }

  carregarUsuarios(page: number = 0, size: number = this.usuariosRows): void {
    this.usuariosLoading = true;
    const sort = `${this.usuariosLastfield},${this.usuariosLastSort}`;
    this.masterEmpresaService.listarUsuarios(this.empresaId, page, size, sort).subscribe({
      next: (resposta) => {
        this.usuarios = resposta.content || [];
        this.usuariosTotal = resposta.totalElements || 0;
        this.usuariosFirst = page * size;
        this.usuariosLoading = false;
      },
      error: () => {
        this.usuarios = [];
        this.usuariosTotal = 0;
        this.usuariosLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail: this.translate.instant('master.usuarios.mensagens.erro_carregar'),
        });
      },
    });
  }

  onUsuariosPageChange(evento: any): void {
    this.usuariosRows = evento.rows;
    this.carregarUsuarios(evento.page, evento.rows);
  }

  /** Ativar/inativar reflete no limite do plano, entao o resumo e relido junto. */
  alternarStatus(usuario: MasterUsuarioDTO): void {
    const novoStatus = !usuario.ativo;
    this.statusEmProgresso.add(usuario.id);
    this.usuarioService.ativarOuInativar(usuario.id, novoStatus).subscribe({
      next: () => {
        usuario.ativo = novoStatus;
        this.statusEmProgresso.delete(usuario.id);
        this.carregarResumo();
      },
      error: (err) => {
        this.statusEmProgresso.delete(usuario.id);
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail: err?.error?.message || this.translate.instant('master.usuarios.mensagens.erro_status'),
        });
      },
    });
  }

  // --- Senha ---

  abrirDialogSenha(usuario: MasterUsuarioDTO): void {
    this.usuarioDaSenha = usuario;
    this.senhaNova = '';
    this.senhaConfirmacao = '';
    this.senhaSubmetida = false;
    this.senhaDialogVisible = true;
  }

  get senhaValida(): boolean {
    return this.senhaNova.length >= 8 && this.senhaNova === this.senhaConfirmacao;
  }

  salvarSenha(): void {
    this.senhaSubmetida = true;
    if (!this.usuarioDaSenha || !this.senhaValida) return;

    this.salvandoSenha = true;
    this.usuarioService.atualizarSenha(this.usuarioDaSenha.id, this.senhaNova).subscribe({
      next: () => {
        this.salvandoSenha = false;
        this.senhaDialogVisible = false;
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.sucesso'),
          detail: this.translate.instant('master.usuarios.mensagens.senha_alterada'),
        });
      },
      error: (err) => {
        this.salvandoSenha = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail: err?.error?.message || this.translate.instant('master.usuarios.mensagens.erro_senha'),
        });
      },
    });
  }

  // --- Dados ---

  abrirDialogEdicao(usuario: MasterUsuarioDTO): void {
    this.usuarioEditando = usuario;
    this.formUsuario = {
      nome: usuario.pessoa?.nome || '',
      email: usuario.email || '',
      telefone: usuario.pessoa?.telefone || '',
      ddi: usuario.pessoa?.ddi || '',
    };
    this.edicaoSubmetida = false;
    this.edicaoDialogVisible = true;
  }

  get edicaoValida(): boolean {
    return !!this.formUsuario.email?.trim() && !!this.formUsuario.nome?.trim();
  }

  /**
   * Salva os dados do usuario.
   *
   * Sao dois cadastros: o acesso (e-mail, username) fica no usuario e o nome/telefone na pessoa —
   * mesma divisao da tela de usuarios do cliente, por isso os dois PUTs. A pessoa vai primeiro:
   * se ela falhar, o e-mail do acesso nao muda sozinho e a tela continua coerente.
   */
  salvarDadosDoUsuario(): void {
    this.edicaoSubmetida = true;
    if (!this.usuarioEditando || !this.edicaoValida) return;

    const usuario = this.usuarioEditando;
    const telefoneLimpo = this.formUsuario.telefone?.replace(/\D/g, '') || '';
    const ddiLimpo = telefoneLimpo ? (this.formUsuario.ddi || '595').replace('+', '') : undefined;

    const salvarUsuario = () => {
      this.usuarioService.put(`/usuario/${usuario.id}`, {
        username: usuario.username,
        email: this.formUsuario.email.trim(),
        nome: this.formUsuario.nome.trim(),
        ddi: ddiLimpo,
        telefone: telefoneLimpo || undefined,
        ativo: usuario.ativo,
      }).subscribe({
        next: () => {
          this.salvandoUsuario = false;
          this.edicaoDialogVisible = false;
          this.carregarUsuarios(this.usuariosFirst / this.usuariosRows, this.usuariosRows);
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('common.sucesso'),
            detail: this.translate.instant('master.usuarios.mensagens.dados_alterados'),
          });
        },
        error: (err) => {
          this.salvandoUsuario = false;
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('common.erro'),
            detail: err?.error?.message || this.translate.instant('master.usuarios.mensagens.erro_dados'),
          });
        },
      });
    };

    this.salvandoUsuario = true;
    const pessoa = usuario.pessoa;
    if (!pessoa?.id) {
      salvarUsuario();
      return;
    }

    // O GET devolve enums como objeto ({ enum, nome }); o backend espera a string.
    const normalizarEnum = (valor: any): any =>
      valor && typeof valor === 'object' && 'enum' in valor ? valor.enum : valor;

    this.pessoaService.put(`/pessoas/${pessoa.id}`, {
      ...pessoa,
      nome: this.formUsuario.nome.trim(),
      email: this.formUsuario.email.trim(),
      ddi: ddiLimpo,
      telefone: telefoneLimpo || undefined,
      tipoPessoa: normalizarEnum(pessoa['tipoPessoa']),
      sexo: normalizarEnum(pessoa['sexo']),
      tipoNaturezaFiscal: normalizarEnum(pessoa['tipoNaturezaFiscal']),
    } as any).subscribe({
      next: () => salvarUsuario(),
      error: (err) => {
        this.salvandoUsuario = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail: err?.error?.message || this.translate.instant('master.usuarios.mensagens.erro_dados'),
        });
      },
    });
  }

  // =========================================================================
  // Sessoes de acesso da empresa
  // =========================================================================

  /**
   * Sessoes abertas dos usuarios da empresa.
   *
   * Traz tambem as que ja nao seguram vaga (token vencido ou sem uso recente): a coluna de
   * situacao separa uma coisa da outra, e esconder as mortas faria a tela mentir sobre o que
   * existe no banco.
   */
  carregarSessoes(): void {
    this.sessoesLoading = true;
    this.masterEmpresaService.listarSessoes(this.empresaId).subscribe({
      next: (sessoes) => {
        this.sessoes = sessoes || [];
        this.sessoesLoading = false;
      },
      error: () => {
        this.sessoes = [];
        this.sessoesLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail: this.translate.instant('master.sessoes.mensagens.erro_carregar'),
        });
      },
    });
  }

  /**
   * Encerra a sessao e libera a vaga na hora.
   *
   * Confirmacao antes: quem estiver usando o sistema naquele aparelho cai na tela de login na
   * requisicao seguinte, e isso nao pode acontecer por um clique errado.
   */
  encerrarSessao(sessao: MasterSessaoDTO): void {
    this.confirmationService.confirm({
      header: this.translate.instant('master.sessoes.confirmacao.titulo'),
      message: this.translate.instant('master.sessoes.confirmacao.mensagem', {
        usuario: sessao.nome || sessao.username,
        plataforma: this.rotuloDaPlataforma(sessao.plataforma),
      }),
      acceptLabel: this.translate.instant('master.sessoes.confirmacao.sim'),
      rejectLabel: this.translate.instant('common.cancelar'),
      icon: 'pi pi-exclamation-triangle',
      accept: () => this.confirmarEncerramento(sessao),
    });
  }

  private confirmarEncerramento(sessao: MasterSessaoDTO): void {
    this.sessoesEmProgresso.add(sessao.id);
    this.masterEmpresaService.encerrarSessao(this.empresaId, sessao.id).subscribe({
      next: () => {
        this.sessoesEmProgresso.delete(sessao.id);
        this.sessoes = this.sessoes.filter((s) => s.id !== sessao.id);
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.sucesso'),
          detail: this.translate.instant('master.sessoes.mensagens.encerrada'),
        });
      },
      error: () => {
        this.sessoesEmProgresso.delete(sessao.id);
        // Pode ter sido encerrada por outro caminho (logout do proprio cliente, expiracao):
        // reler e mais util do que insistir numa linha que talvez ja nem exista.
        this.carregarSessoes();
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail: this.translate.instant('master.sessoes.mensagens.erro_encerrar'),
        });
      },
    });
  }

  /** WEB e MOBILE sao as duas vagas; o rotulo vem do i18n para nao aparecer o enum na tela. */
  rotuloDaPlataforma(plataforma: string): string {
    return plataforma === 'MOBILE'
      ? this.translate.instant('master.sessoes.plataformas.mobile')
      : this.translate.instant('master.sessoes.plataformas.web');
  }
}
